/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult
} from "@tanstack/react-query";

import { IOptionItem } from "@/components/ui/dropdown/types";
import { COUNTRY_DATA } from "@/constants/dataset";
import { Timezone } from "@/constants/dateTime";
import {
  buildFilterAsDatasetPayload,
  buildFilterFromResponse
} from "@/helpers/filterHelper";
import { useMap } from "@/hooks/map/core/useMapContext";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import {
  AdministrativeArea,
  AdministrativeAreaExtract
} from "@/types/adminArea";
import { GetAnalysisConfigurationListParams } from "@/types/analysis";
import { DatasetFilterPayload, H3Configuration } from "@/types/dataset";
import {
  AnalysisConfigurationDetail,
  AnalysisConfigurationListResponse,
  ConfigurationPayload,
  DatasetConfigAttributeResponse,
  GetConfigurationParams,
  GridConfigResponse
} from "@/types/project";
import { CustomError } from "@/utils/errors";

import { getAdminAreaByIdApi } from "../adminArea/adminAreaApi";
import {
  getDatasetMetadata,
  streamDatasetRawData
} from "../dataset/datasetApi";
import {
  CONFIGURATION_PER_PAGE,
  deleteConfigurationDetailApi,
  getAllConfigurationApi,
  getAnalysisConfig,
  getConfigurationApi,
  getConfigurationDetailApi,
  getSettingsApi,
  saveConfigurationApi,
  updateConfigurationApi
} from "./configurationApi";

export const CONFIGURATION_KEYS = {
  configList: ["analysis-configs"] as const,
  configDetail: ["analysis-configs-detail"] as const
} as const;

export const useGetConfigurationList = (
  params?: GetAnalysisConfigurationListParams,
  options?: Omit<
    UseQueryOptions<
      AnalysisConfigurationListResponse,
      Error,
      AnalysisConfigurationListResponse
    >,
    "queryKey" | "queryFn"
  >
): UseQueryResult<AnalysisConfigurationListResponse, Error> => {
  return useQuery({
    queryKey: [CONFIGURATION_KEYS.configList],
    queryFn: () => getAllConfigurationApi(params),
    retry: 1,
    retryDelay: 1000,
    ...options
  });
};

const createSelectValueItem = (
  area?: AdministrativeArea
): IOptionItem | null =>
  area
    ? {
        label: area.name,
        value: area.id,
        code: area.full_code,
        adm_unit: area.adm_unit
      }
    : null;

export const optimizeSelectValue = (
  adminAreaData: AdministrativeAreaExtract
): {
  lv0: IOptionItem | null;
  lv1: IOptionItem | null;
  lv2: IOptionItem | null;
  lv3: IOptionItem | null;
  lv4: IOptionItem | null;
} => {
  const result = {
    lv0:
      COUNTRY_DATA.find(
        (country) => country.value === adminAreaData.country_id
      ) ?? COUNTRY_DATA[0], // Default to "Global"
    lv1: null,
    lv2: null,
    lv3: null,
    lv4: null
  };

  if (!adminAreaData) return result;
  const currentLevel = adminAreaData.level || 4;
  (result as any)[`lv${currentLevel}`] = createSelectValueItem(adminAreaData);

  if (adminAreaData.parents && adminAreaData.parents.length > 0) {
    adminAreaData.parents.forEach((parent) => {
      if (parent?.level) {
        (result as any)[`lv${parent.level}`] = createSelectValueItem(parent);
      }
    });
  }
  return result;
};

function mapAttributesWithFilters(
  attributes: DatasetConfigAttributeResponse[],
  timezone?: Timezone
): DatasetFilterPayload[] {
  return attributes
    .filter((attr) => attr.filter?.criteria)
    .map<DatasetFilterPayload>((attr) => ({
      id: attr.id.split("_")?.[0],
      filter: buildFilterAsDatasetPayload(
        buildFilterFromResponse(attr.filter!),
        timezone
      ),
      attribute_code: attr?.attribute_code
    }));
}

function getUniqueFilters(
  filters: DatasetFilterPayload[]
): DatasetFilterPayload[] {
  const uniqueFiltersMap = new Map<string, DatasetFilterPayload>();

  for (const payload of filters) {
    const filterId = payload.id;
    const existingFilter = uniqueFiltersMap.get(filterId);
    if (
      Array.isArray(payload?.filter) &&
      Array.isArray(existingFilter?.filter)
    ) {
      uniqueFiltersMap.set(filterId, {
        ...payload,
        filter: [...existingFilter.filter, ...payload.filter],
        attribute_code: ""
      });
    } else {
      uniqueFiltersMap.set(filterId, payload);
    }
  }

  return Array.from(uniqueFiltersMap.values());
}

function streamDatasetWithUniqueFilters(
  id: string,
  filters: DatasetFilterPayload[],
  aggregationConfig?: H3Configuration | null
) {
  return streamDatasetRawData({
    datasetId: id,
    filterPayload: getUniqueFilters(filters),
    h3Configuration: aggregationConfig
  });
}

// TODO: can remove this function later after refactor add dataset is fully tested
const fetchConfigurationDetail = async (
  id?: string,
  zoomLevel?: number,
  timezone?: Timezone
): Promise<AnalysisConfigurationDetail> => {
  if (!id) {
    return Promise.reject(new Error("No ID provided"));
  }

  let analysisConfigurationDetailData: AnalysisConfigurationDetail;

  // Fetch the configuration details
  try {
    analysisConfigurationDetailData = await getConfigurationDetailApi(id);
  } catch (error) {
    return Promise.reject(
      error instanceof Error ? error : new Error(String(error))
    );
  }

  if (
    !analysisConfigurationDetailData.datasets ||
    analysisConfigurationDetailData.datasets.length === 0
  ) {
    return {
      ...analysisConfigurationDetailData,
      datasetsDetail: [],
      datasetsData: [],
      datasets: []
    };
  }

  // Get dataset available and map to ids
  const datasetIds = analysisConfigurationDetailData?.datasets
    ?.filter((dataset) => dataset?.available)
    ?.map((availableDataset) => ({
      id: availableDataset.id?.split("_")?.[0],
      filters: mapAttributesWithFilters(availableDataset.attributes, timezone),
      gridConfig: availableDataset.grid_config ?? null
    }));

  const catchmentDataset =
    analysisConfigurationDetailData?.area_input?.catchment?.dataset;
  const mappedCatchmentDataset = {
    id: catchmentDataset?.id,
    filters: mapAttributesWithFilters(
      catchmentDataset?.attributes || [],
      timezone
    )
  };

  const getAggregationConfig = (gridConfig: GridConfigResponse | null) => {
    if (gridConfig) {
      return {
        aggregationType: gridConfig?.aggregation_type,
        resolution: gridConfig?.level
      };
    }
    return null;
  };

  const [
    datasetsDetail,
    datasetsData,
    catchmentDatasetDetail,
    catchmentDatasetData
  ] = await Promise.all([
    Promise.all(datasetIds.map(async ({ id }) => getDatasetMetadata(id))),
    Promise.all(
      datasetIds.map(({ id, filters: filterPayload, gridConfig }) => {
        if (filterPayload) {
          return streamDatasetWithUniqueFilters(
            id,
            filterPayload,
            getAggregationConfig(gridConfig)
          );
        }
        return streamDatasetRawData({
          datasetId: id,
          h3Configuration: getAggregationConfig(gridConfig)
        });
      })
    ),
    (async () => {
      if (mappedCatchmentDataset.id) {
        return getDatasetMetadata(mappedCatchmentDataset.id);
      }
      return undefined;
    })(),
    (async () => {
      if (mappedCatchmentDataset.id) {
        return streamDatasetWithUniqueFilters(
          mappedCatchmentDataset.id,
          mappedCatchmentDataset.filters
        );
      }
      return undefined;
    })()
  ]);

  let analysisConfigurationDetail: AnalysisConfigurationDetail = {
    ...analysisConfigurationDetailData,
    datasetsDetail,
    datasetsData,
    catchmentDatasetDetail,
    catchmentDatasetData
  };

  const adminAreaId = analysisConfigurationDetailData.area_input?.adm_area_id;
  if (!adminAreaId) return analysisConfigurationDetail;

  try {
    // Fetch administrative area data
    const adminAreaData: AdministrativeAreaExtract = await getAdminAreaByIdApi(
      adminAreaId,
      zoomLevel
    );

    analysisConfigurationDetail = {
      ...analysisConfigurationDetail,
      adminAreaData: {
        selectValue: optimizeSelectValue(adminAreaData),
        geoData: {
          data: [adminAreaData]
        }
      }
    };
  } catch (error) {
    console.error("Error fetching admin area data:", error);

    // If admin area data fails, we can return analysisConfigurationDetail as is, without modifying the admin area data
    return analysisConfigurationDetail;
  }

  return analysisConfigurationDetail;
};

export const useGetConfigurationByID = (
  options?: UseMutationOptions<
    AnalysisConfigurationDetail,
    CustomError,
    { id?: string; timezone?: Timezone }
  >
): UseMutationResult<
  AnalysisConfigurationDetail,
  CustomError,
  { id?: string; timezone?: Timezone }
> => {
  const { zoomLevel } = useMap();
  const { isRefactorAddDatasetEnabled } = useAppFeatures();
  return useMutation({
    mutationFn: async ({ id, timezone }) => {
      if (!id) throw new Error("ID is required");

      if (isRefactorAddDatasetEnabled) {
        try {
          return await getConfigurationDetailApi(id);
        } catch (error) {
          const customError = error as CustomError;
          throw new CustomError(
            "Failed to fetch configuration detail",
            customError?.code
          );
        }
      } else {
        // TODO: can remove this function later after refactor add dataset is fully tested
        return fetchConfigurationDetail(id, zoomLevel, timezone);
      }
    },
    retry: 1,
    retryDelay: 1000,
    ...options
  });
};

export const useGetSettings = (
  options?: Omit<
    | Promise<unknown>[]
    | UseQueryOptions<
        { max_catchments: number } | null,
        Error,
        { max_catchments: number } | null
      >,
    "queryKey" | "queryFn"
  >
): UseQueryResult<{ max_catchments: number } | null, Error> => {
  return useQuery({
    queryKey: ["getSettings"],
    queryFn: getSettingsApi,
    ...options
  });
};

export const useDeleteConfigurationByID = (
  options?: Omit<UseMutationOptions<void, Error, string>, "mutationFn">
): UseMutationResult<void, Error, string> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteConfigurationDetailApi(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["config-list-infinite"] });
    },
    retry: 0,
    ...options
  });
};

export const useSaveConfigurationMutation = (
  options?: UseMutationOptions<
    AnalysisConfigurationDetail,
    CustomError,
    ConfigurationPayload,
    unknown
  >
) => {
  return useMutation({
    mutationFn: async (payload: ConfigurationPayload) => {
      const { id } = payload;
      const response = id
        ? await updateConfigurationApi(payload)
        : await saveConfigurationApi(payload);
      return response;
    },
    ...options
  });
};

export const useGetInfiniteConfiguration = (
  params?: GetConfigurationParams,
  config?: UseInfiniteQueryOptions<
    AnalysisConfigurationListResponse,
    Error,
    InfiniteData<AnalysisConfigurationListResponse>
  >
): UseInfiniteQueryResult<
  InfiniteData<AnalysisConfigurationListResponse>,
  Error
> => {
  return useInfiniteQuery<
    AnalysisConfigurationListResponse,
    Error,
    InfiniteData<AnalysisConfigurationListResponse>
  >({
    queryKey: ["config-list-infinite", params],
    queryFn: ({ pageParam }) =>
      getConfigurationApi(pageParam as number, params),
    initialPageParam: 1,
    getNextPageParam: (
      lastPage: AnalysisConfigurationListResponse,
      allPages
    ) => {
      if (lastPage?.data?.length < CONFIGURATION_PER_PAGE) return undefined;
      return allPages.length + 1;
    },
    retry: 1,
    staleTime: 0,
    retryDelay: 1000,
    ...config
  });
};

export const useGetAnalysisConfigMutation = (
  options?: Omit<
    UseMutationOptions<
      { configuration: string },
      Error,
      { analysisId: string }
    >,
    "mutationFn"
  >
) => {
  return useMutation({
    mutationFn: async (payload: { analysisId: string }) => {
      return getAnalysisConfig(payload.analysisId);
    },
    ...options
  });
};
